import { supabase } from '@/integrations/supabase/client';
import { BaseService } from './BaseService';

export interface AdminAction {
  id: string;
  admin_id: string;
  action_type: string;
  target_user_id?: string;
  action_data: Record<string, any>;
  reason?: string;
  created_at: string;
}

export interface UserSuspension {
  id: string;
  user_id: string;
  suspended_by: string;
  reason: string;
  suspended_at: string;
  expires_at?: string;
  is_permanent: boolean;
  appeal_notes?: string;
}

class AdminActionService extends BaseService {
  constructor() {
    super('AdminActionService');
  }

  async logAction(
    actionType: string,
    targetUserId?: string,
    reason?: string,
    actionData: Record<string, any> = {}
  ): Promise<void> {
    return this.execute(
      async () => {
        const user = await this.getCurrentUser();

        const { error } = await supabase
          .from('admin_actions')
          .insert({
            admin_id: user.id,
            action_type: actionType,
            target_user_id: targetUserId,
            reason,
            action_data: actionData
          });

        if (error) throw error;
      },
      { action: 'logAction', metadata: { actionType, targetUserId } }
    );
  }

  async getAdminActions(limit: number = 100): Promise<AdminAction[]> {
    return this.execute(
      async () => {
        const { data, error } = await supabase
          .from('admin_actions')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(limit);

        if (error) throw error;
        return (data || []) as AdminAction[];
      },
      { action: 'getAdminActions', metadata: { limit } }
    );
  }

  async suspendUser(
    userId: string,
    reason: string,
    expiresAt?: string,
    isPermanent: boolean = false
  ): Promise<void> {
    return this.execute(
      async () => {
        const user = await this.getCurrentUser();

        const { error } = await supabase
          .from('user_suspensions')
          .insert({
            user_id: userId,
            suspended_by: user.id,
            reason,
            expires_at: expiresAt,
            is_permanent: isPermanent
          });

        if (error) throw error;

        await this.logAction('suspend_user', userId, reason, {
          expires_at: expiresAt,
          is_permanent: isPermanent
        });
      },
      { action: 'suspendUser', metadata: { userId, isPermanent } }
    );
  }

  async unsuspendUser(suspensionId: string, userId: string): Promise<void> {
    return this.execute(
      async () => {
        const { error } = await supabase
          .from('user_suspensions')
          .delete()
          .eq('id', suspensionId);

        if (error) throw error;

        await this.logAction('unsuspend_user', userId, 'Suspension removed');
      },
      { action: 'unsuspendUser', metadata: { suspensionId, userId } }
    );
  }

  async getUserSuspensions(userId: string): Promise<UserSuspension[]> {
    return this.execute(
      async () => {
        const { data, error } = await supabase
          .from('user_suspensions')
          .select('*')
          .eq('user_id', userId)
          .order('suspended_at', { ascending: false });

        if (error) throw error;
        return (data || []) as UserSuspension[];
      },
      { action: 'getUserSuspensions', metadata: { userId } }
    );
  }

  async isUserSuspended(userId: string): Promise<boolean> {
    return this.execute(
      async () => {
        const { data, error } = await supabase
          .rpc('is_user_suspended', { user_id_input: userId });

        if (error) throw error;
        return data || false;
      },
      { action: 'isUserSuspended', metadata: { userId } }
    );
  }
}

export const adminActionService = new AdminActionService();
